import React from "react";
import { get } from "lodash";
import ReactPlayer from "react-player";

import reducers from "@/Apps/Pages/WebComponents/v2/Button/reducers";
import { IconTypes } from "@/Apps/Pages/WebComponents/v2/Icon/types";
import Icon from "@/Apps/Pages/WebComponents/v2/Icon/live";
import { parseLiquidObjectWithLiquidJS } from "@/Apps/Pages/Page/LiquidRenderer";
// const parseLiquidObject = (obj: any) => obj;

// Safe React Player wrapper that handles loading and errors
const SafeReactPlayer = ({ url, ...props }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    // Reset states when URL changes
    setIsLoading(true);
    setHasError(false);
  }, [url]);

  if (hasError) {
    // Fallback to native video element
    return (
      <video
        src={url}
        controls={props.controls}
        muted={props.muted}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        onError={(e) => {
          console.error('Native video element error:', e);
        }}
      >
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <ReactPlayer
      url={url}
      {...props}
      onReady={() => setIsLoading(false)}
      onError={(error) => {
        console.error('React Player error:', error);
        setHasError(true);
        setIsLoading(false);
      }}
    />
  );
};

// Error boundary component for React Player
class VideoErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Video Player Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback to a simple video element
      const { settings, muted, controls } = this.props;
      const videoUrl = settings?.src?.mp4;
      
      if (!videoUrl) {
        return (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}>
            <p>Video not available</p>
          </div>
        );
      }

      return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <video
            src={videoUrl}
            controls={controls}
            muted={muted}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              console.error('Video element error:', e);
            }}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    return this.props.children;
  }
}

export class VideoContent extends React.Component {
  constructor(props) {
    super(props);
    this.handleProgress = this.handleProgress.bind(this);
    this.state = {
      triggeredYet: false,
      playerError: false,
    };
  }

  handleProgress(e) {
    const {
      settings,
      getComponentSettings,
      updateComponentSettings,
    } = this.props;
    const { actions, triggerAfter = 0 } = settings;
    // console.log(e.playedSeconds);
    if (actions && e.playedSeconds > triggerAfter && !this.state.triggeredYet) {
      const doTheseActions = actions.filter(
        (itm) => itm.behavior === "playProgress"
      );

      this.setState({ triggerYet: true }, () => {
        doTheseActions.forEach((action) =>
          reducers([], {
            ...action,
            settings,
            updateComponentSettings,
            getComponentSettings,
          })
        );
      });
    }
  }

  handleError = (error) => {
    console.error('React Player error:', error);
    this.setState({ playerError: true });
  }

  render() {
    const { settings, muted, controls } = this.props;

    const videoStyle = {
      maxWidth: "100%",
      maxHeight: "100%",
      width: "100%",
      height: "100%",
      position: "absolute" as const,
      top: 0,
      left: 0,
      zIndex: 0,
    };

    const padding = settings.properties ? settings.properties.padding : 0;

    const properties = settings.hasOwnProperty("properties")
      ? settings.properties
      : false;

    const playIconStyle = {
      color: "rgb(255,255,255,0.9)",
      fontSize: "64pt",
      position: "absolute" as const,
      transform: `translate(-50%, -50%)`,
    };

    const width = get(properties, "width", "100%");

    // If player has errored, show fallback
    if (this.state.playerError) {
      const videoUrl = settings?.src?.mp4;
      if (!videoUrl) {
        return (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}>
            <p>Video not available</p>
          </div>
        );
      }

      return (
        <div style={parseLiquidObjectWithLiquidJS({ padding })}>
          <div
            style={parseLiquidObjectWithLiquidJS({
              ...properties,
              width,
              marginTop: 0,
              padding: 0,
              position: "relative" as const,
              paddingTop: `${100 / (1280 / 720)}%`,
            })}
          >
            <video
              src={videoUrl}
              controls={controls}
              muted={muted}
              style={parseLiquidObjectWithLiquidJS(videoStyle)}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      );
    }

    return (
      <div style={parseLiquidObjectWithLiquidJS({ padding })}>
        <div
          style={parseLiquidObjectWithLiquidJS({
            ...properties,
            width,
            marginTop: 0,
            padding: 0,
            position: "relative" as const,
            paddingTop: `${100 / (1280 / 720)}%`,
          })}
        >
          <VideoErrorBoundary settings={settings} muted={muted} controls={controls}>
            <SafeReactPlayer
              url={settings.src.mp4}
              style={parseLiquidObjectWithLiquidJS(videoStyle)}
              light={get(settings, "properties.backgroundImage", "")
                .replace("url(", "")
                .replace(")", "")}
              width="100%"
              height="100%"
              muted={muted}
              controls={controls}
              ref={(rp) => (this.rp = rp)}
              onProgress={this.handleProgress}
              onError={this.handleError}
              playIcon={
                <div>
                  <Icon
                    type={IconTypes.PlayCircleOutline}
                    style={parseLiquidObjectWithLiquidJS({
                      ...playIconStyle,
                      ...get(settings, "playIconStyle", {}),
                      color: `rgb(0,0,0,0.15)`,
                      filter: "blur(2px)",
                      margin: "1px 0 0 1px",
                    })}
                  />
                  <Icon
                    type={IconTypes.PlayCircleOutline}
                    style={parseLiquidObjectWithLiquidJS({
                      ...playIconStyle,
                      ...get(settings, "playIconStyle", {}),
                    })}
                  />
                </div>
              }
            />
          </VideoErrorBoundary>
        </div>
      </div>
    );
  }
}

// VideoContent.defaultProps = {
//   muted: false,
//   controls: false,
// };

export const Video = (props) => {
  const { settings } = props;

  const displayStyle = {
    display: get(settings, "properties.display"),
    position: get(settings, "properties.position", "relative"),
    width: get(settings, "properties.width"),
    // height: get(settings, "properties.height"),
  };

  const marginTop = get(settings, "properties.marginTop", 0);
  const textAlign = get(settings, "properties.textAlign", "inherit");
  const height = "100%";

  // const width = get(settings, "properties.width", "100%");
  // const display = width === "100%" ? "block" : "inline-block"; // video is invisible if width:100% & display: 'inline-block', so made it conditional

  return (
    <div data-testid="WC-VIDEO-LIVE" style={parseLiquidObjectWithLiquidJS({ ...displayStyle, marginTop, textAlign, height })}>
      <VideoContent {...props} muted={false} controls />
    </div>
  );
};

export default Video;
